import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from 'src/services/auth.service';
import { getLogger } from 'src/utils/logger';
import { BaseFormState } from 'src/models/form-types';
import { usePublicTrial } from 'src/contexts/PublicTrialContext';
import { GENERIC_ERROR_MESSAGE } from 'src/utils/errorMessages';

const logger = getLogger('useDocumentForm');

interface UseDocumentFormOptions<FormData, SubmitResponse> {
  initialData: FormData;
  submitService: (data: FormData, token: string) => Promise<any>;
  onSubmitSuccess?: (response: SubmitResponse) => void;
}

export function useDocumentForm<FormData, SubmitResponse>({
  initialData,
  submitService,
  onSubmitSuccess,
}: UseDocumentFormOptions<FormData, SubmitResponse>) {
  const [formState, setFormState] = useState<BaseFormState<FormData>>({
    formData: initialData,
    activeStep: 0,
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
    validationErrors: {},
  });

  const { isAuthenticated } = useAuth0();
  const { callApi, loading } = useAuthenticatedApi();
  const { isPublicTrial, requestTrialSignup } = usePublicTrial();

  // Update form data
  const updateFormData = useCallback((newData: Partial<FormData>) => {
    setFormState(prev => ({
      ...prev,
      formData: { 
        ...prev.formData, 
        ...newData 
      }
    }));
  }, []);

  // Handle text input change
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value } as Partial<FormData>);
  }, [updateFormData]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((name: string, checked: boolean, category: keyof FormData) => {
    setFormState(prev => {
      // Handle boolean checkboxes
      if (typeof prev.formData[category] === 'boolean') {
        return {
          ...prev,
          formData: {
            ...prev.formData,
            [category]: checked
          }
        };
      }
      
      // Handle array checkboxes
      if (Array.isArray(prev.formData[category])) {
        const currentArray = [...(prev.formData[category] as string[])];
        
        if (checked) {
          return {
            ...prev,
            formData: {
              ...prev.formData,
              [category]: [...currentArray, name]
            }
          };
        } else {
          return {
            ...prev,
            formData: {
              ...prev.formData,
              [category]: currentArray.filter(item => item !== name)
            }
          };
        }
      }
      
      // Handle string checkboxes (for radio button behavior)
      if (typeof prev.formData[category] === 'string') {
        return {
          ...prev,
          formData: {
            ...prev.formData,
            [category]: checked ? name : ''
          }
        };
      }
      
      return prev;
    });
  }, []);

  // Handle direct array updates (for multi-select components)
  const updateArrayField = useCallback((field: keyof FormData, values: string[]) => {
    updateFormData({ [field]: values } as Partial<FormData>);
  }, [updateFormData]);

  // Navigate between form steps
  const goToStep = useCallback((step: number) => {
    setFormState(prev => ({
      ...prev,
      activeStep: step
    }));
  }, []);

  const nextStep = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      activeStep: prev.activeStep + 1
    }));
  }, []);

  const prevStep = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      activeStep: Math.max(0, prev.activeStep - 1)
    }));
  }, []);

  // Submit the form
  const submitForm = useCallback(async () => {
    setFormState(prev => ({
      ...prev,
      isSubmitting: true,
      submitError: null,
      submitSuccess: false
    }));
    
    try {
      if (isPublicTrial) {
        requestTrialSignup();
        setFormState(prev => ({
          ...prev,
          isSubmitting: false
        }));
        return;
      }

      if (isAuthenticated) {
        // Use the authenticated API call
        const { data, error } = await callApi(submitService, formState.formData);
        
        if (error) {
          logger.error('Error submitting form:', error);
          setFormState(prev => ({
            ...prev,
            isSubmitting: false,
            submitError: error.message || GENERIC_ERROR_MESSAGE
          }));
        } else {
          logger.debug('Form submission successful:', data);
          setFormState(prev => ({
            ...prev,
            isSubmitting: false,
            submitSuccess: true
          }));
          
          if (onSubmitSuccess) {
            onSubmitSuccess(data);
          }
        }
      } else {
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          submitError: 'Authentication required to submit the form'
        }));
      }
    } catch (error) {
      logger.error('Exception submitting form:', error);
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        submitError: GENERIC_ERROR_MESSAGE
      }));
    }
  }, [isPublicTrial, requestTrialSignup, isAuthenticated, callApi, submitService, formState.formData, onSubmitSuccess]);

  return {
    formState,
    updateFormData,
    handleTextChange,
    handleCheckboxChange,
    updateArrayField,
    goToStep,
    nextStep,
    prevStep,
    submitForm,
    isSubmitting: loading || formState.isSubmitting
  };
}
